import { useState } from 'react';
import mainStyles from '../main.module.css';
import styles from './remoteScanModal.module.css';
import ScreenModal from '../../components/ScreenModal/screenModal';
import Button from '../../components/Button/button';
import Input from '../../components/Input/input';
import { useMessageModal } from '../../hooks/modal.hook';
import Validator from '../../utils/validators';
import { useRemoteScanReserveApi } from '../../hooks/api/reserve.hook';
import { ApiError } from '../../services/http';
import AlertModal, { alertModalInitialState, CloseAlertModal, closeModalInitialState } from '../../components/AlertModal';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import { ReservationCartRegisterForm } from '../../services/http/reservationCart.api';
import BaseText from '../../components/BaseText/BaseText';
import Checkbox from '../../components/Checkbox';

export interface ReceiveModalProps {
  documentList: ReservationCartRegisterForm['documentList'],
  onCancel: () => void,
  onSuccess: () => void,
  onError: () => void,
  onDocumentRequestDone: () => void,
}

function Component(props: ReceiveModalProps) {
  const {
    documentList, onCancel, onSuccess, onError, onDocumentRequestDone,
  } = props;
  const openMessageModal = useMessageModal();
  const [alertModal, setAlertModal] = useState(alertModalInitialState);
  const [closeAlertModal, setCloseAlertModal] = useState(closeModalInitialState);
  const [trdContact, setTrdContact] = useState('');
  const [emergencyContacts, setEmergencyContacts] = useState<string | null>(null);
  const [userMemo, setUserMemo] = useState('');
  const [speedScanFlg, setSpeedScanFlg] = useState(0);

  // Apis
  const remoteScanReserveApi = useRemoteScanReserveApi();
  const { request: createLog } = useCreateLogApi();

  const handleSendForm = async () => {
    setAlertModal({ ...alertModal, open: false });
    try {
      await remoteScanReserveApi.request({
        documentList,
        trdContact,
        emergencyContacts: emergencyContacts as string,
        userMemo,
        speedScanFlg,
      });
      onDocumentRequestDone();
      setCloseAlertModal({
        ...closeAlertModal,
        open: true,
        text: '依頼が完了しました。',
        onCancel: onSuccess,
      });
    } catch (e) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: (e as ApiError).message,
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
          onError();
        },
      });
    }
  };

  const handleSubmit = () => {
    if (emergencyContacts == null) {
      openMessageModal('全ての必須項目を入力してください');
      return;
    }
    if (trdContact.length > Validator.contactMessageMax) {
      openMessageModal(`連絡事項を${Validator.contactMessageMax}文字以内で入力してください`);
      return;
    }

    setAlertModal({
      open: true,
      text: '依頼を送信します。よろしいですか？',
      onCancel: () => {
        setAlertModal({ ...alertModal, open: false });
      },
      onConfirm: handleSendForm,
    });
  };

  useEffectOnce(() => {
    createLog(LogFormName.RemoteScanScreen, LogControlName.Show);
  });

  return (
    <ScreenModal
      customStyles={{
        minWidth: '800px',
        maxHeight: '85%',
        border: 'var(--defaultBorder)',
        display: 'flex',
        flexDirection: 'column',
        color: 'initial',
      }}
    >
      <div className={styles['modal-header']}>
        <div className={styles.title}>
          リモートスキャン
        </div>
      </div>
      <div className={styles['modal-body']}>
        <div className={styles['form-row']}>
          <div className={styles['form-left']}>
            <label className={styles.label}>対象件数</label>
          </div>
          <span className={styles.label}>
            {documentList.length.toLocaleString()}
            件
          </span>
        </div>
        <div className={`${styles['form-row']} ${styles['no-padding-bottom']}`}>
          <div className={styles['form-left']}>
            <label className={styles.label}>寺田倉庫への連絡事項</label>
          </div>
          <textarea
            className={mainStyles['text-area']}
            style={{ width: '65%' }}
            value={trdContact}
            placeholder="寺田倉庫へ連絡事項がある場合は入力してください。（100文字以内）"
            onChange={(e) => setTrdContact(e.target.value)}
          />
        </div>
        <div className={styles['form-row']} style={{ marginLeft: '180px' }}>
          <BaseText color="var(--danger)" size="sm">※ 希望納期がある場合にはこちらにご記入ください</BaseText>
        </div>
        <div className={styles['form-row']}>
          <div className={styles['form-left']}>
            <label className={styles.label}>緊急連絡先</label>
            <span>*</span>
          </div>
          <Input
            className={styles.input}
            value={emergencyContacts ?? ''}
            onChange={setEmergencyContacts}
          />
        </div>
        <div className={styles['form-row']}>
          <div className={styles['form-left']}>
            <label className={styles.label}>お客様メモ</label>
          </div>
          <textarea
            className={mainStyles['text-area']}
            style={{ width: '65%' }}
            value={userMemo}
            placeholder="その他、依頼に対して備考がある場合は入力してください。"
            onChange={(e) => setUserMemo(e.target.value)}
          />
        </div>
        <div className={styles['form-row']}>
          <div className={styles['form-left']}>
            <label className={styles.label}>有料オプション</label>
          </div>
          <div className={styles['checkbox-wrapper']}>
            <Checkbox
              label="スピードスキャン"
              value={Boolean(speedScanFlg)}
              onChange={(value) => setSpeedScanFlg(value ? 1 : 0)}
            />
          </div>
        </div>
      </div>
      <div className={styles['modal-footer']}>
        <Button size="smaller" color="lighterGray" onClick={onCancel}>キャンセル</Button>
        <Button size="smaller" onClick={handleSubmit} loading={remoteScanReserveApi.loading}>依頼</Button>
      </div>
      <AlertModal open={alertModal.open} text={alertModal.text} onConfirm={alertModal.onConfirm} onCancel={alertModal.onCancel} />
      <CloseAlertModal open={closeAlertModal.open} text={closeAlertModal.text} onCancel={closeAlertModal.onCancel} closeButtonText="OK" />
    </ScreenModal>
  );
}

export default Component;
